import './App.css';

import { FormattedMessage, IntlProvider } from 'react-intl';
import { Route, Routes } from 'react-router';
import { SessionExpiredDialog } from '@rio-cloud/rio-session-expired-info';
import ApplicationLayout from '@rio-cloud/rio-uikit/ApplicationLayout';
import NotificationsContainer from '@rio-cloud/rio-uikit/NotificationsContainer';
import { ConfigCatProvider, createConsoleLogger, DataGovernance, PollingMode } from 'configcat-react';

import { DEFAULT_LOCALE, extractLanguage } from '../../configuration/lang/lang';
import { isUserSessionExpired } from '../../configuration/login/loginSlice';
import { useAppDispatch, useAppSelector } from '../../configuration/setup/hooks';
import { DisplayMessages, getDisplayMessages, getLocale } from '../../configuration/lang/langSlice';
import { DefaultRedirect } from './routes/DefaultRedirect';
import { RouteUpdater } from './routes/RouteUpdater';

import { getSessionExpiredAcknowledged, hideSessionExpiredDialog } from './appSlice';
import { AppHeader } from './header/AppHeader';
import {
    COMPOUND_MANAGEMENT_ROUTE,
    OOB_INSPECTOR_ROUTE,
    SERVICE_ORDER_ROUTE,
    SERVICE_PROVIDER_ACTIVATION_ROUTE,
    TRANSPORT_ORDER_ROUTE,
    WELCOME_ROUTE,
} from './routes/routes';
import { TransportAssignmentSidebar } from './transportAssignment/sidebar/TransportAssignmentSidebar';
import { TermsAndConditionsModal } from './activation/TermsAndConditionsModal';
import { TransportAssignments } from './transportAssignment/TransportAssignments';
import { useGetFeatureActivationsQuery } from './api/activationApi';
import { CompoundAccess, FeatureActivationStatus } from './activation/activation.types';
import React, { FunctionComponent, useEffect } from 'react';
import { WelcomePage } from './activation/WelcomePage';
import ErrorState from '@rio-cloud/rio-uikit/ErrorState';
import { getErrorMessage } from './notifications/ErrorNotification';
import { ServiceOrderSidebar } from './serviceOrders/serviceOrders/sidebar/ServiceOrderSidebar';
import { config } from '../../config';
import { TransportCapacityOrderSidebar } from './transportAssignment/sidebar/TransportCapacityOrderSidebar';
import { ServiceProviders } from './serviceProviders/ServiceProviders';
import { CompoundManagement } from './compoundManagement/CompoundManagement';
import { VehicleInventorySidebar } from './compoundManagement/inventory/sidebar/VehicleInventorySidebar';
import { DepartedVehiclesSidebar } from './compoundManagement/departed/sidebar/DepartedVehiclesSidebar';
import { IncomingVehiclesSidebar } from './compoundManagement/incoming/sidebar/IncomingVehiclesSidebar';
import { OutgoingVehiclesSidebar } from './compoundManagement/outgoing/sidebar/OutgoingVehiclesSidebar';
import { BulkStatusUpdateDialog } from './compoundManagement/shared/bulkStatusUpdate/BulkStatusUpdateDialog';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { SerializedError } from '@reduxjs/toolkit';
import { reportErrorToSentry } from '../../configuration/setup/sentry';
import { ServiceOptionSidebar } from './serviceOrders/serviceOptions/sidebar/ServiceOptionSidebar';
import { ServiceOrdersOrOptions } from './serviceOrders/ServiceOrdersOrOptions';
import { IncidentBanner } from './notifications/IncidentBanner';
import { OobInspector } from '../OobInspector/OobInspector';

export const App = (): React.ReactElement | null => {
    const userLocale = useAppSelector(getLocale);
    const displayMessages = useAppSelector(getDisplayMessages);

    // We want the `<html lang>` attribute to be in sync with the language currently displayed
    useEffect(() => {
        const language = extractLanguage(userLocale);
        const htmlElement = document.documentElement;
        if (!!language && htmlElement.getAttribute('lang') !== language) {
            htmlElement.setAttribute('lang', language);
        }
    }, [userLocale]);

    if (!displayMessages || userLocale == null) {
        return null;
    }

    const logger = createConsoleLogger(config.configCat.logLevel);

    const isConfigCatOffline = import.meta.env.MODE === 'development' || import.meta.env.MODE === 'test';

    return (
        <IntlProvider defaultLocale={DEFAULT_LOCALE} key={userLocale} locale={userLocale} messages={displayMessages}>
            <ConfigCatProvider
                sdkKey={config.configCat.apiKey}
                pollingMode={PollingMode.LazyLoad}
                options={{
                    cacheTimeToLiveSeconds: config.configCat.pollingFrequencyInSeconds,
                    logger,
                    dataGovernance: DataGovernance.EuOnly,
                    offline: isConfigCatOffline,
                }}
            >
                <AppContent displayMessages={displayMessages} userLocale={userLocale} />
            </ConfigCatProvider>
        </IntlProvider>
    );
};

const AppContent: FunctionComponent<{ displayMessages: DisplayMessages; userLocale: string }> = ({
    displayMessages,
    userLocale,
}) => {
    const featureActivations = useGetFeatureActivationsQuery();

    if (featureActivations.isLoading) {
        return null;
    }
    if (featureActivations.isError) {
        return (
            <AppInitError displayMessages={displayMessages} userLocale={userLocale} error={featureActivations.error} />
        );
    }

    const isActiveForAnySupplier = featureActivations.data?.activationStatus === FeatureActivationStatus.APPROVED;
    const isCompoundManagementAvailable =
        isActiveForAnySupplier && featureActivations.data?.compound === CompoundAccess.ACCESS;

    return (
        <AppLayout
            isActiveForAnySupplier={isActiveForAnySupplier}
            isCompoundManagementAvailable={isCompoundManagementAvailable}
            shouldMountCompoundUi={isCompoundManagementAvailable}
        />
    );
};

type AppLayoutProps = {
    isActiveForAnySupplier: boolean;
    isCompoundManagementAvailable: boolean;
    shouldMountCompoundUi: boolean;
};

const AppLayout: FunctionComponent<AppLayoutProps> = ({
    isActiveForAnySupplier,
    isCompoundManagementAvailable,
    shouldMountCompoundUi,
}) => {
    const dispatch = useAppDispatch();

    const userLocale = useAppSelector(getLocale);
    const isSessionExpired = useAppSelector(isUserSessionExpired);
    const sessionExpiredAcknowledged = useAppSelector(getSessionExpiredAcknowledged);

    const handleSessionExpiredDialogClose = () => dispatch(hideSessionExpiredDialog());
    const showSessionExpired = isSessionExpired && !sessionExpiredAcknowledged;

    return (
        <ApplicationLayout className="Outbound-Portal">
            <ApplicationLayout.Header>
                <AppHeader
                    isActive={isActiveForAnySupplier}
                    isCompoundManagementAvailable={isCompoundManagementAvailable}
                />
            </ApplicationLayout.Header>
            <ApplicationLayout.Sidebar className="right">
                <TransportAssignmentSidebar />
                <TransportCapacityOrderSidebar />
                <ServiceOrderSidebar />
                <ServiceOptionSidebar />
                {shouldMountCompoundUi && (
                    <>
                        <IncomingVehiclesSidebar />
                        <VehicleInventorySidebar />
                        <OutgoingVehiclesSidebar />
                        <DepartedVehiclesSidebar />
                        <BulkStatusUpdateDialog />
                    </>
                )}
            </ApplicationLayout.Sidebar>
            <ApplicationLayout.Body banner={<IncidentBanner />}>
                <NotificationsContainer />
                <TermsAndConditionsModal />
                <SessionExpiredDialog
                    locale={userLocale ?? DEFAULT_LOCALE}
                    onClose={handleSessionExpiredDialogClose}
                    show={showSessionExpired}
                />
                <Routes>
                    <Route path={WELCOME_ROUTE} element={<WelcomePage />} />
                    {isActiveForAnySupplier && (
                        <>
                            <Route path={TRANSPORT_ORDER_ROUTE} element={<TransportAssignments />} />
                            <Route path={SERVICE_ORDER_ROUTE} element={<ServiceOrdersOrOptions />} />
                            <Route path={SERVICE_PROVIDER_ACTIVATION_ROUTE} element={<ServiceProviders />} />
                            <Route path={OOB_INSPECTOR_ROUTE} element={<OobInspector />} />
                        </>
                    )}
                    {isCompoundManagementAvailable && (
                        <Route path={COMPOUND_MANAGEMENT_ROUTE} element={<CompoundManagement />} />
                    )}
                    <Route path="*" element={<DefaultRedirect isActive={isActiveForAnySupplier} />} />
                </Routes>
                <RouteUpdater />
            </ApplicationLayout.Body>
        </ApplicationLayout>
    );
};

const AppInitError: FunctionComponent<{
    displayMessages: DisplayMessages;
    userLocale: string;
    error: FetchBaseQueryError | SerializedError;
}> = ({ displayMessages, userLocale, error }) => {
    useEffect(() => {
        reportErrorToSentry(error);
    }, [error]);

    return (
        <IntlProvider defaultLocale={DEFAULT_LOCALE} key={userLocale} locale={userLocale} messages={displayMessages}>
            <ApplicationLayout className="Outbound-Portal">
                <ApplicationLayout.Body>
                    <ErrorState
                        headline={<FormattedMessage id="intl-msg:common-message.error.generic.headline" />}
                        message={<FormattedMessage id={getErrorMessage(error)} />}
                    />
                </ApplicationLayout.Body>
            </ApplicationLayout>
        </IntlProvider>
    );
};
